import { ReactNode, useRef, useEffect, useState } from "react";
import { Colors } from "../../utils/colors";

export type FlatTabItem = {
  id: string;
  label: string;
};

type FlatTabsProps = {
  items: FlatTabItem[];
  tabItem: string;
  setTabItem: (tabItem: string) => void;
  leftComp?: ReactNode;
  rightComp?: ReactNode;
};

// Tabs with line under spaced around (ie. ProfilePageFull)
const FlatTabs = (props: FlatTabsProps) => {
  const { items, tabItem, setTabItem, leftComp, rightComp } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Edge case on overflow, need to check if overflowing to properly display the tabs with scroll
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const scrollWidth = containerRef.current.scrollWidth;
        setIsOverflowing(scrollWidth > containerWidth);
      }
    };

    // Check immediately and after a short delay to account for rendering
    checkOverflow();
    const timeoutId = setTimeout(checkOverflow, 0);
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [items, leftComp, rightComp]);

  const styles = {
    tabsContainer: {
      width: "100%",
      justifyContent: isOverflowing ? "flex-start" : "center",
    },
    tabItem: {
      justifyContent: "center",
      paddingInline: 10,
      paddingBottom: 12,
      fontSize: 14,
      fontWeight: 500,
      background: "none",
      border: "none",
      outline: "none",
      cursor: "pointer",
      transition: "color 0.3s ease",
      whiteSpace: "nowrap",
    },
    tabItemActive: {
      color: Colors.BLACK,
      borderBottom: `3px solid ${Colors.BLACK}`,
    },
    tabItemInactive: {
      color: Colors.GRAY1,
      borderBottom: `3px solid ${Colors.TRANSPARENT}`,
    },
    compContainer: {
      borderBottom: `3px solid ${Colors.TRANSPARENT}`,
    },
  };

  const handlePress = (id: string) => {
    setTabItem(id);
  };

  return (
    <div
      ref={containerRef}
      className="AlignedRow"
      style={{ ...styles.tabsContainer, overflowX: "auto" }}
    >
      {leftComp ? (
        <div
          style={{ ...styles.tabItem, ...styles.compContainer }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          {leftComp}
        </div>
      ) : null}
      {items.map((item) => (
        <div
          key={item.id}
          className="AlignedRow"
          style={{
            ...styles.tabItem,
            ...(item.id === tabItem
              ? styles.tabItemActive
              : styles.tabItemInactive),
          }}
          onClick={() => handlePress(item.id)}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <span className="bodyMedium">{item.label}</span>
        </div>
      ))}
      {rightComp ? (
        <div
          style={{ ...styles.tabItem, ...styles.compContainer }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          {rightComp}
        </div>
      ) : null}
    </div>
  );
};

export default FlatTabs;
